import ChargeBtn from '@/components/sales/ChargeBtn';
import { selectCurrency } from '@/redux/config/configSlice';
import { updateTicket } from '@/redux/data/dataSlice';
import { TicketItemType, TicketType } from '@/redux/data/dataTypes';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { Badge, Button, Modal, Tag, Typography } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

interface MiniCartPreviewProps {
  activeTicket?: TicketType;
  total: number;
  disableCharge?: boolean;
}

/**
 * Calculates the item total price considering unit pricing
 */
const calculateItemTotal = (item: TicketItemType): number => {
  const price = item.unitPrice ?? item.price;
  return price * item.quantity;
};

const MiniCartPreview = ({ activeTicket, total, disableCharge }: MiniCartPreviewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currency = useAppSelector(selectCurrency);

  const handleChargePress = useCallback(
    (id: string) => {
      navigate(`/ticket/${id}`);
    },
    [navigate]
  );

  const handleClearSale = useCallback(() => {
    if (!activeTicket) return;
    const newTicket = structuredClone(activeTicket);
    newTicket.items = [];
    newTicket.discounts = [];
    newTicket.modifierOptions = [];
    newTicket.createdAt = new Date().toISOString();
    dispatch(updateTicket(newTicket));
  }, [activeTicket, dispatch]);

  return (
    <div className="w-full md:w-[300px] flex-shrink-0 mt-4 md:mt-0">
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:sticky md:top-5">
        <div className="flex items-center justify-between mb-4">
          <Text strong className="text-lg">
            {t('common:current_sale')}
          </Text>
          <Badge
            count={activeTicket?.items?.length || 0}
            className="animate-bounce"
            style={{ backgroundColor: activeTicket?.items?.length ? '#1890ff' : '#d9d9d9' }}
          />
        </div>

        <div className="max-h-[300px] md:max-h-[calc(100vh-300px)] overflow-auto mb-4">
          {activeTicket?.items?.map((item) => {
            const itemPrice = item.unitPrice ?? item.price;
            const itemTotal = calculateItemTotal(item);

            return (
              <div
                key={item.ID}
                className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0"
              >
                <div
                  className={`flex-1 ${
                    item.unitName ? 'bg-blue-50 dark:bg-blue-900/20 p-2 rounded' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-baseline flex-wrap gap-1">
                        <Text className="font-medium">{item.name}</Text>
                        {item.unitName && (
                          <Tag color="blue" className="ml-1 text-xs">
                            {item.unitName}
                          </Tag>
                        )}
                      </div>
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center text-xs">
                          <Text type="secondary">
                            {item.quantity} × {itemPrice.toFixed(2)} {currency}
                          </Text>
                          {item.unitPrice && item.unitPrice !== item.price && (
                            <Text type="secondary" className="ml-1 text-xs line-through">
                              ({item.price.toFixed(2)} {currency}/{t('common:piece')})
                            </Text>
                          )}
                        </div>
                        {item.unitConversionFactor && item.unitConversionFactor !== 1 && (
                          <div className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded inline-block">
                            <Text type="secondary">
                              {t('common:conversion_factor', { factor: item.unitConversionFactor })}
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>
                    <Text strong className="ml-4 whitespace-nowrap">
                      {itemTotal.toFixed(2)} {currency}
                    </Text>
                  </div>
                </div>
              </div>
            );
          })}
          {!activeTicket?.items?.length && (
            <div className="text-center py-8 text-gray-400">
              <ShoppingCartOutlined className="text-4xl mb-2" />
              <Text type="secondary" className="block">
                {t('common:no_items_added')}
              </Text>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center mb-2">
            <Text>{t('common:total')}</Text>
            <Text strong className="text-lg">
              {total.toFixed(2)} {currency}
            </Text>
          </div>

          {activeTicket?.items && activeTicket?.items?.length > 0 && (
            <ChargeBtn onPress={handleChargePress} disabled={disableCharge} />
          )}

          {Boolean(activeTicket?.items?.length) && (
            <Button
              danger
              block
              className="mt-2"
              onClick={() => {
                Modal.confirm({
                  title: t('common:clear_sale_confirmation'),
                  content: t('common:clear_sale_confirmation_desc'),
                  okText: t('common:clear'),
                  okButtonProps: { danger: true },
                  onOk: handleClearSale,
                });
              }}
            >
              {t('common:clear_sale')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiniCartPreview;
